const ALLOWED_TAGS = new Set([
  'a',
  'blockquote',
  'br',
  'code',
  'div',
  'em',
  'h1',
  'h2',
  'h3',
  'h4',
  'hr',
  'li',
  'ol',
  'p',
  'pre',
  'span',
  'strong',
  'sub',
  'sup',
  'table',
  'tbody',
  'td',
  'th',
  'thead',
  'tr',
  'ul',
]);

const GLOBAL_ALLOWED_ATTRS = new Set(['class', 'aria-hidden', 'role', 'colspan', 'rowspan']);
const URL_ALLOWED_ATTRS = new Set(['href', 'target', 'rel']);
const SAFE_PROTOCOLS = ['http:', 'https:', 'mailto:'];

function stripNode(node: Node): void {
  const parent = node.parentNode;
  if (!parent) {
    return;
  }
  while (node.firstChild) {
    parent.insertBefore(node.firstChild, node);
  }
  parent.removeChild(node);
}

function sanitizeAttributes(element: Element): void {
  const attributes = Array.from(element.attributes);
  for (const attr of attributes) {
    const name = attr.name.toLowerCase();
    if (name.startsWith('on')) {
      element.removeAttribute(attr.name);
      continue;
    }
    if (GLOBAL_ALLOWED_ATTRS.has(name)) {
      continue;
    }
    if (element.tagName.toLowerCase() === 'a' && URL_ALLOWED_ATTRS.has(name)) {
      if (name === 'href') {
        const value = attr.value.trim();
        try {
          const resolved = new URL(value, window.location.origin);
          if (!SAFE_PROTOCOLS.includes(resolved.protocol) && !value.startsWith('/')) {
            element.removeAttribute(attr.name);
          }
        } catch {
          if (!value.startsWith('/')) {
            element.removeAttribute(attr.name);
          }
        }
      }
      continue;
    }
    element.removeAttribute(attr.name);
  }

  if (element.tagName.toLowerCase() === 'a') {
    const href = element.getAttribute('href');
    if (href) {
      element.setAttribute('rel', 'noopener noreferrer');
      element.setAttribute('target', '_blank');
    }
  }
}

function sanitizeNode(node: Node): void {
  if (node.nodeType === Node.ELEMENT_NODE) {
    const element = node as Element;
    const tag = element.tagName.toLowerCase();
    if (!ALLOWED_TAGS.has(tag)) {
      stripNode(node);
      return;
    }
    sanitizeAttributes(element);
  }
  const children = Array.from(node.childNodes);
  for (const child of children) {
    sanitizeNode(child);
  }
}

export function sanitizeHtml(html: string): string {
  if (!html || typeof window === 'undefined') {
    return html || '';
  }
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  const body = doc.body;
  const children = Array.from(body.childNodes);
  for (const child of children) {
    sanitizeNode(child);
  }
  return body.innerHTML;
}
